// Faturas / histórico: número, período, emissão e vencimento, status e total.
// Faturas em aberto/aguardando/falhas têm "Pagar" que abre o checkout transparente
// (PaymentIntent da fatura) — sem sair para a página hospedada do Stripe.
import { useState } from 'react';
import { toast } from 'sonner';
import { FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { formatMoney } from '@/lib/money';
import { CheckoutDialog, type CheckoutTarget } from './CheckoutDialog';

export interface InvoiceItem {
  kind: 'plan' | 'addon';
  addon_kind?: string;
  description: string;
  qty: number;
  unit_amount_cents: number;
  amount_cents: number;
  proration?: boolean;
}
export interface Invoice {
  id: string;
  number: number;
  currency: string;
  status: 'open' | 'awaiting_payment' | 'paid' | 'failed' | 'void';
  reason: 'initial' | 'addon' | 'renewal';
  recurring: boolean;
  total_cents: number;
  period_start?: string;
  period_end?: string;
  issue_date?: string;
  due_date?: string;
  paid_at?: string;
  items?: InvoiceItem[];
}

const STATUS_VARIANT: Record<Invoice['status'], 'success' | 'secondary' | 'warning' | 'destructive'> = {
  paid: 'success',
  open: 'warning',
  awaiting_payment: 'secondary',
  failed: 'destructive',
  void: 'secondary',
};
const PAYABLE: Invoice['status'][] = ['open', 'awaiting_payment', 'failed'];

export function InvoiceList({
  invoices,
  locale,
  returnUrl,
  autoPayId,
  onChanged,
}: {
  invoices: Invoice[];
  locale: string;
  returnUrl: string;
  /** ?pay=<id>: abre automaticamente o checkout da fatura. */
  autoPayId?: string;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState('');
  const [target, setTarget] = useState<CheckoutTarget | null>(null);

  const dt = (s?: string) => (s ? new Date(s).toLocaleDateString(locale) : '—');

  const pay = async (inv: Invoice) => {
    setBusy(inv.id);
    try {
      const r = await apiFetch('POST', `/me/invoices/${inv.id}/pay`);
      const cs = (r.data as { client_secret?: string } | undefined)?.client_secret;
      if (!r.ok || !cs) {
        if (r.status === 501) toast.error(t('billing.notConfigured'));
        else toast.error(t('billing.checkoutFailed'));
        return;
      }
      setTarget({
        clientSecret: cs,
        mode: 'payment',
        returnUrl: `${returnUrl}?paid=${inv.id}`,
        title: t('billing.payInvoiceTitle', { number: String(inv.number) }),
        description: t('billing.payInvoiceDesc'),
        confirmLabel: t('billing.checkoutPay', { amount: formatMoney(inv.total_cents, inv.currency) }),
      });
    } finally { setBusy(''); }
  };

  // ?pay=<id>: dispara o pagamento da fatura assim que a lista carrega.
  const [autoTried, setAutoTried] = useState(false);
  if (autoPayId && !autoTried && invoices.length > 0) {
    const inv = invoices.find((i) => i.id === autoPayId);
    setAutoTried(true);
    if (inv && PAYABLE.includes(inv.status)) void pay(inv);
  }

  if (invoices.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('billing.invoicesEmpty')}</p>;
  }

  return (
    <>
      <ul className="flex flex-col divide-y">
        {invoices.map((inv) => (
          <li key={inv.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">
                #{inv.number} · {formatMoney(inv.total_cents, inv.currency)}
                {inv.reason && <span className="ml-2 text-xs font-normal text-muted-foreground">{t(`billing.reason_${inv.reason}`)}</span>}
              </p>
              <p className="text-xs text-muted-foreground">
                {inv.period_start && (
                  <span>{t('billing.invoicePeriod', { from: dt(inv.period_start), to: dt(inv.period_end) })} · </span>
                )}
                {t('billing.invoiceIssued', { date: dt(inv.issue_date) })}
                {inv.due_date && <span> · {t('billing.invoiceDue', { date: dt(inv.due_date) })}</span>}
                {inv.paid_at && <span> · {t('billing.invoicePaid', { date: dt(inv.paid_at) })}</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={STATUS_VARIANT[inv.status]}>{t(`billing.invStatus_${inv.status}`)}</Badge>
              {PAYABLE.includes(inv.status) && (
                <Button size="sm" variant="outline" disabled={busy === inv.id} onClick={() => pay(inv)}>
                  {busy === inv.id ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}
                  {t('billing.invoicePay')}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <CheckoutDialog
        target={target}
        onClose={() => setTarget(null)}
        onSuccess={() => { setTarget(null); toast.success(t('billing.checkoutSuccess')); onChanged(); }}
      />
    </>
  );
}
